<script setup>
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useOptionsStore } from '@/stores/options';
import { usePharmacyStore } from '@/stores/pharmacy';
import { isRole } from '@/utils/roleHelper';
import { useUserStore } from '@/stores/user';

const props = defineProps({
    pharmacy: {
        type: Object,
        required: true,
    },
    mode: {
        type: String,
        default: 'Edit', // 'Create' or 'Edit'
    },
});

const { profile } = storeToRefs(useUserStore());
const { store, update } = usePharmacyStore();
const roles = computed(() => isRole(profile.value));

const form = ref({
    rebate_fee_enabled: false,
});

watch(() => props.pharmacy, (newPharmacy) => {
    if (newPharmacy && props.mode === 'Edit') {
        form.value = { ...newPharmacy };
    }
}, { immediate: true });

const optionsStore = useOptionsStore();
const { pharmacyStatusOptions } = storeToRefs(optionsStore);

const emit = defineEmits(['saved']);

const save = async () => {
    const action = props.mode === 'Create' ? store : update;
    await action({
        ...form.value,
    });

    emit('saved');
};

// This is crucial - explicitly expose the save method
defineExpose({
    save
});
</script>

<template>
<div class="md:grid md:grid-cols-2 gap-x-4">
    <div class="col-span-full text-lg font-bold">
        {{ $t("label.general") }}
    </div>
       
    <StyledInput
        v-model="form.name"
        name="name"
        :label="$t('label.name')" />

    <StyledInput
        v-if="roles.superAdmin() || roles.systemAdmin() || roles.pharmacyAdmin()"
        v-model="form.delivery_rate"
        type="number"
        name="delivery_rate"
        :label="$t('label.delivery_rate')" />

    <StyledCurrency
        v-if="roles.superAdmin() || roles.systemAdmin() || roles.pharmacyAdmin()"
        v-model="form.discount_amount"
        :config="{ precision: 2 }"
        :placeholder="$t('label.discount_amount')"
        name="discount_amount"
        :label="$t('label.discount_amount')" />

    <StyledInput
        v-if="roles.superAdmin() || roles.systemAdmin() || roles.pharmacyAdmin()"
        v-model="form.rebate_fee_percent"
        type="number"
        name="rebate_fee_percent"
        :label="$t('label.rebate_fee_percent')" />
    
    <StyledRoundSwitch
        v-if="roles.superAdmin() || roles.systemAdmin() || roles.pharmacyAdmin()"
        v-model="form.rebate_fee_enabled"
        name="rebate_fee_enabled"
        :label="$t('label.enable_rebate_fee')"
        inset
        color="primary" />

    <StyledDropdown
        v-model="form.status_id"
        :options="pharmacyStatusOptions"
        name="status_id"
        :label="$t('label.status')" />

    <div class="col-span-full text-lg font-bold">
        {{ $t("label.contact") }}
    </div>
    <StyledMask
        v-model="form.contact_phone"
        name="contact_phone"
        placeholder="###-###-####"
        :options="{ mask: '###-###-####' }"
        :label="$t('label.contact_phone')" />

    <StyledInput
        v-model="form.contact_first_name"
        name="contact_first_name"
        :label="$t('label.contact_first_name')" />

    <StyledInput
        v-model="form.contact_last_name"
        name="contact_last_name"
        :label="$t('label.contact_last_name')" />

    <StyledInput
        v-model="form.contact_email"
        name="contact_email"
        :label="$t('label.contact_email')" />
</div>
</template>
